// Bulk import for Result Mapping entries (e.g. 30 RIASEC character cards prepared in one file).
// Accepts a plain array of entries, { result_mapping: [...] }, or an instrument export
// ({ instrument: { result_mapping: [...] } }). Entries are matched by result_key: imported
// fields overwrite text, but an image URL left empty in the file keeps the uploaded image.

const TEXT_FIELDS = [
  "title_id", "title_en", "subtitle_id", "subtitle_en", "reflection_id", "reflection_en",
  "badge_id", "badge_en", "color",
];
const IMAGE_FIELDS = ["hero_image_id_url", "hero_image_en_url", "share_image_id_url", "share_image_en_url"];

export function extractImportedMapping(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.result_mapping)
      ? data.result_mapping
      : Array.isArray(data?.instrument?.result_mapping)
        ? data.instrument.result_mapping
        : null;
  if (!list) throw new Error("no_result_mapping");
  return list.filter((entry) => entry && typeof entry === "object" && String(entry.result_key || "").trim());
}

// Optional Learn More list headings shipped in the same file ({ learn_more_lists: [...] } at the
// top level or under instrument). Keys are cleaned like the server does, so entry extras such as
// `strengths_id` keep matching their heading. Returns null when the file has none.
export function extractImportedLists(data) {
  const raw = Array.isArray(data?.learn_more_lists)
    ? data.learn_more_lists
    : Array.isArray(data?.instrument?.learn_more_lists) ? data.instrument.learn_more_lists : null;
  if (!raw) return null;
  const seen = new Set();
  const lists = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const key = String(item.key || "").trim().toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 40);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    lists.push({ key, label_id: String(item.label_id || "").trim().slice(0, 80), label_en: String(item.label_en || "").trim().slice(0, 80) });
    if (lists.length >= 6) break; // the server keeps at most 6 lists
  }
  return lists;
}

export function mergeResultMapping(current, imported, makeId) {
  const out = (current || []).map((entry) => ({ ...entry }));
  const indexByKey = new Map(out.map((entry, i) => [String(entry.result_key || "").trim(), i]));
  let added = 0;
  let updated = 0;
  for (const raw of imported) {
    const key = String(raw.result_key).trim();
    const patch = { result_key: key };
    for (const field of TEXT_FIELDS) if (typeof raw[field] === "string") patch[field] = raw[field];
    for (const field of IMAGE_FIELDS) if (typeof raw[field] === "string" && raw[field].trim()) patch[field] = raw[field].trim();
    if (raw.extras && typeof raw.extras === "object" && !Array.isArray(raw.extras)) patch.extras = raw.extras;
    if (indexByKey.has(key)) {
      const i = indexByKey.get(key);
      out[i] = { ...out[i], ...patch, extras: { ...(out[i].extras || {}), ...(patch.extras || {}) } };
      updated += 1;
    } else {
      out.push({
        id: makeId(),
        title_id: "", title_en: "", subtitle_id: "", subtitle_en: "", reflection_id: "", reflection_en: "",
        hero_image_id_url: "", hero_image_en_url: "", share_image_id_url: "", share_image_en_url: "",
        color: "", badge_id: "", badge_en: "", pdf_template_id: null, extras: {},
        ...patch,
      });
      indexByKey.set(key, out.length - 1);
      added += 1;
    }
  }
  return { mapping: out, added, updated };
}
